import { EAssetType, IItemPlanDto, IPlanAssetDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { FireBaseService } from "../../../../firebase/firebase";
import { PaymentService } from "../../../../services/payment-service";
import { SubscriptionService } from "../../../../services/subscription-service";
import { UserService } from "../../../../services/user-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import { KuikaSkeletonCardGrid, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaH1 from "../../../ui-kit/typography/H1/H1";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaH6 from "../../../ui-kit/typography/H6/H6";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { PaymentUpdateModal } from "./payment-update-modal";
import "./subscription-plans.scss";
import { SubscriptionUpdateModal } from "./subscription-update-modal";

declare let window: any;

interface IPaymentUpdateModalMessage {
  type: EPaymentUpdateModalType;
  message: string;
  headerMessage: string;
}

enum EPaymentUpdateModalType {
  Success = 0,
  Updated = 1,
  Error = 2,
  Cancelled = 3
}

export interface ISubscriptionPlansState {
  isDataLoading: boolean;
  nextSubscription: IItemPlanDto;
  desiredSubscription: IItemPlanDto;
  isModalActive: boolean;
  isSeatChangeModalActive: boolean;
  activeTab: "individual" | "enterprise";
  planFeatures: Record<string, string[]>;
  subscriptionPlans: IItemPlanDto[];
  isPaymentUpdateModalActive: boolean;
  paymentUpdateModalMessage?: IPaymentUpdateModalMessage;
  isCancelModalActive: boolean;
  currentStripeSubscription: any;
  nextStripeSubscription: any;
}

export interface ISubscriptionPlansProps {}

/** The plan tabs in their on-screen order, so a tab's cards slide the way the selection moved. */
const PLAN_TAB_ORDER: string[] = ["individual", "enterprise"];

class SubscriptionPlans extends React.PureComponent<
  ISubscriptionPlansProps & IWithContextProp & IWithHistoryProp & IWithSDProps,
  ISubscriptionPlansState
> {
  formRef = React.createRef<any>();

  constructor(props: ISubscriptionPlansProps & IWithContextProp & IWithHistoryProp & IWithSDProps) {
    super(props);

    this.state = {
      isDataLoading: false,
      nextSubscription: undefined as unknown as IItemPlanDto,
      desiredSubscription: undefined as unknown as IItemPlanDto,
      isModalActive: false,
      isSeatChangeModalActive: false,
      activeTab: "individual",
      planFeatures: {},
      subscriptionPlans: [],
      isPaymentUpdateModalActive: false,
      isCancelModalActive: false,
      currentStripeSubscription: undefined as unknown as any,
      nextStripeSubscription: undefined as unknown as any
    };
  }

  async componentDidMount() {
    const firebase = new FireBaseService();
    firebase.logEvent("subscription_plans_page_loaded");
    this.loadSubscription();
    this.loadSubscriptionPlans();
    this.loadPlanCsv();
    const searchParams = new URLSearchParams(window.location.search);
    const subscriptionId = searchParams.get("status");
    const isChangePlan = searchParams.get("change_plan") === "true";
    if (subscriptionId) {
      if (subscriptionId === "successSubscriptionCheckout") {
        this.setState({
          paymentUpdateModalMessage: {
            type: isChangePlan ? EPaymentUpdateModalType.Updated : EPaymentUpdateModalType.Success,
            message: isChangePlan ? "Subscription successfully updated" : "Subscription successfully created",
            headerMessage: isChangePlan ? "Subscription updated" : "Successfully payment"
          },
          isPaymentUpdateModalActive: true
        });
      } else if (subscriptionId === "failedSubscriptionCheckout") {
        this.setState({
          paymentUpdateModalMessage: {
            type: EPaymentUpdateModalType.Error,
            message: "Subscription creation failed",
            headerMessage: "Payment failed"
          },
          isPaymentUpdateModalActive: true
        });
      } else if (subscriptionId === "cancelSubscription") {
        this.setState({
          paymentUpdateModalMessage: {
            type: EPaymentUpdateModalType.Cancelled,
            message: "Your subscription has been cancelled.",
            headerMessage: "Subscription Cancelled"
          },
          isPaymentUpdateModalActive: true
        });
      } else if (subscriptionId === "upgradedSubscription") {
        this.setState({
          paymentUpdateModalMessage: {
            type: EPaymentUpdateModalType.Updated,
            message: "Subscription successfully updated",
            headerMessage: "Subscription updated"
          },
          isPaymentUpdateModalActive: true
        });
      }
    }
  }

  async loadSubscriptionPlans() {
    const res = await SubscriptionService.GetSubscriptionPlans();
    if (res && res.status === 200) {
      const plans = res.data as IItemPlanDto[];
      this.setState({ subscriptionPlans: plans });
    }
  }

  async loadSubscription() {
    this.setState({ isDataLoading: true });
    try {
      let currentSub = this.state.subscriptionPlans[0]; // defualt olarak free olduğunu varsay
      let nextSub = undefined as unknown as IItemPlanDto; // önümüzdeki ay farklı bir pakete geçmeyeceğini varsay
      const currentStripeSubscription = await (await PaymentService.GetCurrentSubscription()).data; // gerçek subscription varmı diye al
      if (currentStripeSubscription && currentStripeSubscription.items && currentStripeSubscription.items.length > 0) {
        // varsa currentSubu gerçek subscriptionla eşitle
        currentSub = this.state.subscriptionPlans.filter(
          (sub) => sub.stripeId == currentStripeSubscription.items[0].price.id
        )[0];
      }
      const nextStripeSubscription = await (await PaymentService.GetNextSubscription()).data;
      if (nextStripeSubscription && nextStripeSubscription.items && nextStripeSubscription.items.length > 0) {
        nextSub = this.state.subscriptionPlans.filter(
          (sub) => sub.stripeId == nextStripeSubscription.items[0].price.id
        )[0];
      }
      this.setState({
        isDataLoading: false,
        nextSubscription: nextSub,
        currentStripeSubscription,
        nextStripeSubscription
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.setState({ isDataLoading: false });
    }
  }

  async loadPlanCsv() {
    try {
      const csvUrl = new URL("./plans.csv", import.meta.url).toString();
      const res = await fetch(csvUrl);
      const text = await res.text();
      const rows = text
        .split(/\r?\n/)
        .map((r) => r.trim())
        .filter((r) => r.length > 0);
      if (!rows.length) return;
      const header = rows[0].split(";").map((s) => s.trim());
      const planHeaders = header.slice(1);
      const featuresByPlan: Record<string, string[]> = {};
      planHeaders.forEach((p) => (featuresByPlan[this.normalizePlanKey(p)] = []));
      for (let i = 1; i < rows.length; i++) {
        const parts = rows[i].split(";").map((s) => s.trim());
        if (parts.length < 2) continue;
        for (let c = 1; c < parts.length; c++) {
          const planKey = this.normalizePlanKey(planHeaders[c - 1]);
          const cell = parts[c];
          if (!cell || cell === "-") continue;
          featuresByPlan[planKey]?.push(cell);
        }
      }
      this.setState({ planFeatures: featuresByPlan });
    } catch (e) {}
  }

  normalizePlanKey = (name: string) =>
    (name || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9 +()]/g, "")
      .trim();

  categorizeFeatures = (features: string[]) => {
    const categorized: Record<"Platform" | "Cloud" | "Support" | "Other", string[]> = {
      Platform: [],
      Cloud: [],
      Support: [],
      Other: []
    };
    if (!features || !features.length) return categorized;
    for (const feat of features) {
      const text = (feat || "").toLowerCase();
      const isPlatform =
        text.includes("developer") ||
        text.includes("custom ai") ||
        text.includes("ai token") ||
        text.includes("token limit") ||
        text.includes("bpm") ||
        text.includes("on-prem") ||
        text.includes("on prem") ||
        text.includes("onprem") ||
        text.includes("code export");
      const isCloud =
        text.includes("environment") ||
        text.includes("enviroment") ||
        text.includes("data storage") ||
        text.includes("request limit") ||
        text.includes("live app") ||
        text.includes("vcpu") ||
        text.includes("ram") ||
        text.includes("security") ||
        text.includes("request");
      const isSupport =
        text.includes("live support") ||
        text.includes("response time") ||
        text.includes("resolution time") ||
        text.includes("dedicated account manager") ||
        text.includes("ticket system") ||
        text.includes("support");
      if (isPlatform) {
        categorized.Platform.push(feat);
        continue;
      }
      if (isCloud) {
        categorized.Cloud.push(feat);
        continue;
      }
      if (isSupport) {
        categorized.Support.push(feat);
        continue;
      }
      categorized.Other.push(feat);
    }
    return categorized;
  };

  private formatDate = (dateValue?: any): string => {
    if (!dateValue) return "";
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short" }).toUpperCase();
    const year = String(d.getFullYear());
    return `${day} ${month} ${year}`;
  };

  private getRenewDateText = (): string => {
    const current = this.state.currentStripeSubscription;
    if (current?.currentPeriodEnd) return this.formatDate(current.currentPeriodEnd);
    const now = new Date();
    const nextMonthFirstDay = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const daysInNextMonth = new Date(nextMonthFirstDay.getFullYear(), nextMonthFirstDay.getMonth() + 1, 0).getDate();
    const targetDay = Math.min(now.getDate(), daysInNextMonth);
    const target = new Date(nextMonthFirstDay.getFullYear(), nextMonthFirstDay.getMonth(), targetDay);
    return this.formatDate(target);
  };

  private getCancelAtText = (): string => {
    const current = this.state.currentStripeSubscription;
    if (current?.cancelAtPeriodEnd && current?.cancelAt) return this.formatDate(current.cancelAt);
    return "";
  };

  handleCancelSubscriptionClick = () => {
    this.setState({ isCancelModalActive: true });
  };

  closeCancelModal = () => {
    this.setState({ isCancelModalActive: false });
  };

  cancelSubscription = async () => {
    const message: IPaymentUpdateModalMessage = {
      type: EPaymentUpdateModalType.Cancelled,
      message: "Your subscription has been cancelled.",
      headerMessage: "Subscription Cancelled"
    };
    this.setState({
      isCancelModalActive: false,
      isDataLoading: true,
      isPaymentUpdateModalActive: true,
      paymentUpdateModalMessage: message
    });
    try {
      await PaymentService.CancelSubscription();
      await this.loadSubscriptionPlans();
      await this.loadSubscription();
      await UserService.GetUserInfoForce();
      this.setState({ isDataLoading: false, isCancelModalActive: false });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.setState({ isDataLoading: false, isPaymentUpdateModalActive: false, isCancelModalActive: false });
    }
  };

  renderAssetValue = (asset: IPlanAssetDto) => {
    if (!asset) return null;
    switch (asset.assetType) {
      case EAssetType.upto:
        if (asset.assetQty == -1) return <span className="subscription-plans__asset-name">Unlimited</span>;
        if (asset.assetQty == -2) return <span className="subscription-plans__asset-name">Extra Fee</span>;
        if (asset.assetQty == 1) return <span className="subscription-plans__asset-name">{asset.assetQty}</span>;
        return (
          <span className="subscription-plans__asset-name">{`Max. ${asset.assetQty} ${asset.extraLabel ?? ""}`}</span>
        );
      case EAssetType.yesNo:
        return asset.assetQty == 1 ? <KuikaSvg name="check" width={16} height={16} color="var(--blue-5)" /> : <></>;
      case EAssetType.gb:
        if (asset.assetQty > 250)
          return <span className="subscription-plans__asset-name">{asset.assetQty / 1000} GB</span>;
        return <span className="subscription-plans__asset-name">{asset.assetQty} MB</span>;
    }
    return asset.assetQty;
  };

  renderPlanAssets = () => [];

  renderPlanAssetsInner = () => [];

  private isPlanSelected = (plan: IItemPlanDto) => {
    const currentPlanId = window?.userInfo?.currentPlanId || "";
    if (!currentPlanId) return plan.planPrice <= 0;
    return plan.stripeId === currentPlanId;
  };

  private belongsToTab = (planName: string, tab: string): boolean => {
    const name = (planName || "").toLowerCase();
    const isEnterpriseGroup = name.includes("enterprise");
    return tab === "enterprise" ? isEnterpriseGroup : !isEnterpriseGroup;
  };

  private renderPrice = (plan: IItemPlanDto) => {
    const isCustom = plan.planName?.includes("Enterprise");
    if (plan.planPrice > 0) {
      if (isCustom) return <KuikaH1>-</KuikaH1>;
      const currency = (plan.planPriceCurrency as any) === "USD" ? "$" : plan.planPriceCurrency;
      return (
        <KuikaH1>
          {currency}
          {plan.planPrice}
          <KuikaTextMedium>/mo</KuikaTextMedium>
        </KuikaH1>
      );
    }
    return <KuikaH1>Free</KuikaH1>;
  };

  private getSelectedPlanName = (): string => {
    const providerPlans = this.state.subscriptionPlans || [];
    const selectedPlan = providerPlans.find((p) => this.isPlanSelected(p));
    return selectedPlan?.planName || "";
  };

  private getFeatureListForPlan = (planName: string) => {
    const key = this.normalizePlanKey(planName);
    return this.state.planFeatures[key] || [];
  };

  /** One plan tab's cards. Rendered per pane, so the pane that leaves keeps drawing its own plans. */
  renderCards = (tab: string) => {
    const providerPlans = this.state.subscriptionPlans as IItemPlanDto[];
    const filtered = providerPlans.filter((p) => this.belongsToTab(p.planName, tab));
    const hasNonFreePlan = Boolean(window?.userInfo?.currentPlanId && window.userInfo.currentPlanId !== "");
    const contactUrl = "https://www.kuika.com/tr/sirket/iletisime-gec";
    return filtered
      ?.sort((a, b) => a.displayOrder - b.displayOrder)
      ?.map((plan) => {
        const selected = this.isPlanSelected(plan);
        const features = this.getFeatureListForPlan(plan.planName);
        const buttonLabel = selected
          ? "Your Plan"
          : hasNonFreePlan || plan.planName?.includes("Enterprise")
            ? "Contact us"
            : `Upgrade to ${plan.planName}`;
        const handleClickPlanButton = () => {
          if (!selected && (hasNonFreePlan || plan.planName?.includes("Enterprise"))) {
            window.open(contactUrl, "_blank");
            return;
          }
          this.setState({ desiredSubscription: plan, isModalActive: !selected });
        };
        return (
          <div
            key={plan.id}
            className={`subscription-plans__card ${selected ? "subscription-plans__card--selected" : ""}`}
            role="listitem"
          >
            {plan.planName?.includes("Pro") && <div className="subscription-plans__popular">Popular</div>}
            <KuikaH5>{plan.planName}</KuikaH5>
            {this.renderPrice(plan)}
            {/* <KuikaText style={{ textAlign: "center", color: "var(--dark-7)" }}>{plan.planDescription}</KuikaText> */}
            <KuikaButton
              type={selected ? "secondary" : "primary"}
              style={{ width: 200 }}
              onClick={handleClickPlanButton}
            >
              {selected && <KuikaSvg name="check" width={16} height={16} style={{ marginRight: 8 }} />}
              {buttonLabel}
            </KuikaButton>
            <div className="subscription-plans__features">
              {(() => {
                const categorized = this.categorizeFeatures(features);
                const sections: Array<"Platform" | "Cloud" | "Support"> = ["Platform", "Cloud", "Support"];
                return (
                  <>
                    {sections.map((section) => {
                      const list = categorized[section];
                      if (!list || !list.length) return null;
                      return (
                        <div key={section} className="subscription-plans__category" role="group" aria-label={section}>
                          <KuikaTextBold className="subscription-plans__category-title">{section}</KuikaTextBold>
                          {list.map((feat, idx2) => (
                            <div key={`${section}-${idx2}`} className="subscription-plans__feature-row">
                              <KuikaTextMedium style={{ color: "var(--dark-9)" }}>{feat}</KuikaTextMedium>
                              <KuikaSvg name="info-square-rounded" width={16} height={16} color="var(--dark-5)" />
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {categorized.Other &&
                      categorized.Other.map((feat, idx3) => (
                        <div key={`Other-${idx3}`} className="subscription-plans__feature-row">
                          <KuikaTextMedium style={{ color: "var(--dark-9)" }}>{feat}</KuikaTextMedium>
                          <KuikaSvg name="info-square-rounded" width={16} height={16} color="var(--dark-5)" />
                        </div>
                      ))}
                  </>
                );
              })()}
            </div>
          </div>
        );
      });
  };

  closePaymentUpdateModal = () => {
    this.setState({ isPaymentUpdateModalActive: false });
  };

  render() {
    // "subscriptionPage" marks the page root in both the loading and loaded
    // states so navigation can be verified independently of whether the remote
    // plan data has finished loading.
    if (this.state.isDataLoading) {
      // The plan cards' shape while the subscription loads (L7).
      return (
        <div className="kuika-settings-panel" data-testid="subscriptionPage">
          <KuikaSkeletonRegion label="Loading subscription plans">
            <KuikaSkeletonCardGrid count={4} />
          </KuikaSkeletonRegion>
        </div>
      );
    }
    const hasNonFreePlan = Boolean(window?.userInfo?.currentPlanId && window.userInfo.currentPlanId !== "");
    const isCanceled = Boolean(this.state.currentStripeSubscription?.cancelAtPeriodEnd);
    return (
      <>
        <div className="kuika-settings-panel" data-testid="subscriptionPage">
          <div className="kuika-settings-panel__header">
            <div className="kuika-settings-panel__header-left">
              <KuikaSvg width={24} height={24} name="calendar-dollar" />
              <KuikaH4 data-testid="subscriptionPlansPageTitle">Subscription</KuikaH4>
            </div>
          </div>
          <div className="kuika-subscription-plans">
            <div className="kuika-subscription-plans__header">
              <div className="kuika-settings-panel__header-left">
                <KuikaH4>Plans</KuikaH4>
              </div>
              {hasNonFreePlan && (
                <div className="kuika-subscription-plans__header-right">
                  {!isCanceled && (
                    <KuikaButton className="subscription-plans__header-right-button" type="primary">
                      <KuikaSvg name="calendar-event" width={16} height={16} />
                      Renew · {this.getRenewDateText()}
                    </KuikaButton>
                  )}
                  {isCanceled && (
                    <KuikaTextMedium style={{ color: "var(--red-6)", marginRight: 8 }}>
                      Cancels on · {this.getCancelAtText()}
                    </KuikaTextMedium>
                  )}
                  {!isCanceled && Boolean(window?.userInfo?.currentPlanId && window.userInfo.currentPlanId !== "") && (
                    <KuikaButton
                      type="secondary"
                      style={{ marginLeft: 8 }}
                      onClick={this.handleCancelSubscriptionClick}
                      aria-label="Cancel subscription"
                    >
                      Cancel subscription
                    </KuikaButton>
                  )}
                </div>
              )}
            </div>
            <KuikaTabs
              className="subscription-plans__tabs"
              size="lg"
              options={[
                { value: "individual", label: <KuikaH6>Individual</KuikaH6> },
                { value: "enterprise", label: <KuikaH6>Enterprise</KuikaH6> }
              ]}
              value={this.state.activeTab}
              onChange={(v) => this.setState({ activeTab: v as any })}
            />
            <KuikaTabContent
              activeKey={this.state.activeTab}
              order={PLAN_TAB_ORDER}
              render={(tab: string): JSX.Element => (
                <div className="subscription-plans__plans" role="list">
                  {this.renderCards(tab)}
                </div>
              )}
            />
          </div>
          {this.state.isModalActive && (
            <SubscriptionUpdateModal
              showModal={this.state.isModalActive}
              nextSubscription={this.state.nextSubscription}
              desiredSubscription={this.state.desiredSubscription}
              closeModal={() => this.setState({ isModalActive: false })}
            />
          )}
          {/* {this.state.isSeatChangeModalActive && (
            <SubscriptionSeatUpdate
              showModal={this.state.isSeatChangeModalActive}
              currentSubscription={this.state.currentSubscription}
              nextSubscription={this.state.nextSubscription}
              desiredSubscription={this.state.desiredSubscription}
              closeModal={() => this.setState({ isSeatChangeModalActive: false })}
            />
          )} */}
          {this.state.isPaymentUpdateModalActive && (
            <PaymentUpdateModal
              showModal={this.state.isPaymentUpdateModalActive}
              closeModal={this.closePaymentUpdateModal}
              paymentUpdateModalMessage={this.state.paymentUpdateModalMessage}
            ></PaymentUpdateModal>
          )}
          {this.state.isCancelModalActive && (
            <DeleteModal
              itemName={this.getSelectedPlanName()}
              triggerModal={this.state.isCancelModalActive}
              closeModal={this.closeCancelModal}
              deleteItem={this.cancelSubscription}
              deleteType="subscription"
              type="cancel"
            />
          )}
        </div>
      </>
    );
  }
}

const plansPage = withContext<ISubscriptionPlansProps>(
  withManagement<ISubscriptionPlansProps>(
    withHistory<ISubscriptionPlansProps>(withSD<ISubscriptionPlansProps>(SubscriptionPlans))
  )
);
export { plansPage as SubscriptionPlans };
